import { useEffect, useRef, useState } from "react";

/**
 * useCountUp — animates from 0 → `value` with smooth easing.
 *
 * @param {number} value     Target number.
 * @param {object} opts
 * @param {number} opts.duration  Milliseconds (default 1800).
 * @param {boolean} opts.enabled  Pause until true (e.g. IntersectionObserver).
 * @returns {number}  Current animated value.
 */
export function useCountUp(value, { duration = 1800, enabled = true } = {}) {
  const [current, setCurrent] = useState(0);
  const startRef = useRef(null);
  const fromRef = useRef(0);
  const rafRef = useRef(null);
  const targetRef = useRef(value);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof value !== "number" || !isFinite(value)) {
      setCurrent(0);
      return undefined;
    }
    fromRef.current = current;
    targetRef.current = value;
    startRef.current = null;

    const step = (ts) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const next = fromRef.current + (targetRef.current - fromRef.current) * eased;
      setCurrent(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setCurrent(targetRef.current);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enabled, duration]);

  return current;
}

/** Compact Indian-style formatter — 1,21,000 → "1.21 L", 12,500,000 → "1.25 Cr". */
export function formatCompactIN(n) {
  if (n == null || !isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)} K`;
  return Math.round(n).toLocaleString("en-IN");
}

/** Plain Indian number with commas — 17943911 → "1,79,43,911". */
export function formatFullIN(n) {
  if (n == null || !isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-IN");
}

/** "Updated 3 mins ago" / "Live" relative formatter. */
export function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 30) return "Live";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  return `${day} day${day === 1 ? "" : "s"} ago`;
}
